/**
 * Wall lines. The Wall tool lays a line of one-tile pieces whose materials follow the player's age:
 * Settlement Age fences with timber posts, Feudal Age fences with stone pillars, Castle Age onward
 * stone walls with stone pillars. A join piece goes at each end and every JOIN_EVERY tiles.
 */

/** Longest wall line placed in one go, in tiles. */
export const MAX_WALL_TILES = 30;
/** Spacing of join pieces (posts or pillars) along a line. */
export const JOIN_EVERY = 5;

export interface WallKinds {
  segment: string;
  join: string;
  name: string;
}

export function wallKindsForAge(age: number): WallKinds {
  if (age <= 0) return { segment: 'fence', join: 'fencePost', name: 'Fence' };
  if (age === 1) return { segment: 'fence', join: 'stonePillar', name: 'Fence and Pillars' };
  return { segment: 'stoneWall', join: 'stonePillar', name: 'Stone Wall' };
}

/**
 * Tiles from start to end as a 4-connected line (no diagonal gaps), clipped to MAX_WALL_TILES.
 * Integer only, so the simulation and the client agree exactly.
 */
export function wallTiles(sx: number, sy: number, ex: number, ey: number): [number, number][] {
  const out: [number, number][] = [[sx, sy]];
  const dx = Math.abs(ex - sx);
  const dy = Math.abs(ey - sy);
  const stepX = ex > sx ? 1 : -1;
  const stepY = ey > sy ? 1 : -1;
  let x = sx;
  let y = sy;
  let err = dx - dy;
  while ((x !== ex || y !== ey) && out.length < MAX_WALL_TILES) {
    // Step along whichever axis keeps the line closest to the ideal, one axis at a time.
    if (2 * err > -dy && (2 * err >= dx || y === ey) && x !== ex) {
      err -= dy;
      x += stepX;
    } else {
      err += dx;
      y += stepY;
    }
    out.push([x, y]);
  }
  return out;
}

/** Pieces for a line: [kind key, tx, ty], with join pieces at both ends and every JOIN_EVERY tiles. */
export function planWall(age: number, sx: number, sy: number, ex: number, ey: number): [string, number, number][] {
  const k = wallKindsForAge(age);
  const tiles = wallTiles(sx, sy, ex, ey);
  return tiles.map(([x, y], i) => [i === 0 || i === tiles.length - 1 || i % JOIN_EVERY === 0 ? k.join : k.segment, x, y]);
}
